import { useMemo, useState } from 'react';
import type { GeoBrowseEnrichmentRow } from '../../types';
import { useGeoBrowseQuery } from '../../hooks/useGeoBrowseThresholds';
import { fetchEnrichment } from '../../services/geoBrowseApi';
import BrowseDataTable, { type BrowseColumn } from '../BrowseDataTable';
import EnrichmentBarChart from './EnrichmentBarChart';
import styles from '../../GeoBrowse.module.css';

const COLUMNS: BrowseColumn<GeoBrowseEnrichmentRow>[] = [
  { id: 'gene_set_name', label: 'gene_set_name', accessor: r => r.gene_set_name },
  { id: 'collection', label: 'collection', accessor: r => r.collection },
  { id: 'nes', label: 'nes', accessor: r => r.nes.toFixed(2) },
  { id: 'nom_pval', label: 'nom_pval', accessor: r => r.nom_pval.toFixed(4) },
  { id: 'fdr_qval', label: 'fdr_qval', accessor: r => r.fdr_qval.toFixed(3) },
  { id: 'tag_pct', label: 'tag_pct', accessor: r => r.tag_pct.toFixed(2) },
  { id: 'gene_pct', label: 'gene_pct', accessor: r => r.gene_pct.toFixed(2) },
  { id: 'lead_genes', label: 'lead_genes', accessor: r => r.lead_genes },
];

const COLLECTION_INFO: Record<'CURATED' | 'REACTOME', string> = {
  CURATED:
    'This collection contains diseases and traits from DISGENET expert-curated gene-disease associations (non-null scores). Positive NES (right/red) indicates enrichment (up), negative NES (left/blue) indicates depletion (down).',
  REACTOME:
    'Reactome pathway gene sets. Positive NES (right/red) indicates enrichment in the pathway; negative NES (left/blue) indicates depletion.',
};

type EnrichmentPanelProps = {
  cui: string;
  accession: string;
  comparisonId?: string;
};

export default function EnrichmentPanel({ cui, accession, comparisonId }: EnrichmentPanelProps) {
  const [collection, setCollection] = useState<'CURATED' | 'REACTOME'>('CURATED');
  const [fdr, setFdr] = useState('0.25');
  const [topN, setTopN] = useState('10');

  const fdrNum = Number(fdr);
  const topNNum = Number(topN);
  const queryFdr = Number.isFinite(fdrNum) ? fdrNum : 0.25;
  const queryTopN = Number.isFinite(topNNum) ? topNNum : 10;

  const { data, loading, error } = useGeoBrowseQuery(
    () => fetchEnrichment(cui, accession, { collection, fdr: queryFdr, top_n: queryTopN }, comparisonId),
    [cui, accession, comparisonId, collection, queryFdr, queryTopN],
  );

  const tableRows = useMemo(() => data ?? [], [data]);

  return (
    <>
      <div className={styles.enrichmentControls}>
        <label>
          Collection:
          <select
            value={collection}
            onChange={e => setCollection(e.target.value as 'CURATED' | 'REACTOME')}
          >
            <option value="CURATED">CURATED</option>
            <option value="REACTOME">REACTOME</option>
          </select>
        </label>
        <label>
          FDR q-val threshold:
          <input
            type="number"
            step="0.01"
            min="0"
            max="1"
            value={fdr}
            onChange={e => setFdr(e.target.value)}
          />
        </label>
        <label>
          Top N per direction:
          <input
            type="number"
            step="1"
            min="1"
            max="50"
            value={topN}
            onChange={e => setTopN(e.target.value)}
          />
        </label>
      </div>

      <div className={styles.enrichmentInfoBanner}>{COLLECTION_INFO[collection]}</div>

      {loading && <div className={styles.loading}>Loading enrichment…</div>}
      {error && <div className={styles.error}>{error}</div>}

      {data && (
        <>
          <EnrichmentBarChart rows={data} collection={collection} fdr={queryFdr} />
          <div className={styles.enrichmentTableWrap}>
            <BrowseDataTable rows={tableRows} columns={COLUMNS} rowKey={r => r.gene_set_id} />
          </div>
        </>
      )}
    </>
  );
}
